/* P-Fab model finder: asks the storefront for real model-page matches before navigating. */
(() => {
  if (window.PfabModelChooser) return;

  const text = (value) => {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = String(value || '');
    return decoder.value.replace(/\s+/g, ' ').trim();
  };

  const scopedSearchUrl = (query, scope, pageOnly) => {
    const params = new URLSearchParams();
    params.set('q', [text(scope), text(query)].filter(Boolean).join(' '));
    if (pageOnly) params.set('type', 'page');
    params.set('options[prefix]', 'last');
    return `/search?${params.toString()}`;
  };

  const hasExactModel = (title, query) => {
    const raw = text(query).toLowerCase();
    if (/^\d+$/.test(raw)) {
      const escapedNumber = raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(^|[^0-9])(?:[a-z]+[^a-z0-9]*)?${escapedNumber}(?=$|[^a-z0-9])`, 'i').test(text(title));
    }
    const chunks = raw
      .replace(/([a-z])([0-9])/g, '$1 $2')
      .replace(/([0-9])([a-z])/g, '$1 $2')
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
      .map((chunk) => chunk.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!chunks.length) return false;
    const pattern = chunks.join('[^a-z0-9]*');
    return new RegExp(`(^|[^a-z0-9])${pattern}(?=$|[^a-z0-9])`, 'i').test(text(title));
  };

  const matchesScope = (title, scope) => {
    const words = text(scope).toLowerCase().split(/\s+/).filter(Boolean);
    const candidate = text(title).toLowerCase();
    return words.every((word) => candidate.includes(word));
  };

  const ensureStyles = () => {
    if (document.getElementById('PfabModelChooserStyles')) return;
    const style = document.createElement('style');
    style.id = 'PfabModelChooserStyles';
    style.textContent = `
      .pf-model-choices { margin-top: 1rem; padding: 1.1rem; border: 1px solid rgba(244,108,9,.7); border-radius: .8rem; color: #fff; background: rgba(5,18,50,.96); box-shadow: 0 1.4rem 3rem rgba(0,0,0,.28); }
      .pf-model-choices__title { display:block; margin-bottom:.75rem; font-weight:800; font-size:1.35rem; }
      .pf-model-choices__grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(18rem,1fr)); gap:.65rem; }
      .pf-model-choices__link { display:flex; min-height:4rem; align-items:center; padding:.8rem 1rem; border:1px solid rgba(255,255,255,.28); border-radius:.55rem; color:#fff; background:rgba(255,255,255,.06); font-weight:700; line-height:1.2; text-decoration:none; }
      .pf-model-choices__link:hover { border-color:#f46c09; background:rgba(244,108,9,.18); color:#fff; }
    `;
    document.head.appendChild(style);
  };

  const renderChoices = (form, query, choices, fallbackUrl) => {
    let panel = form.parentElement?.querySelector('.pf-model-choices');
    if (panel) panel.remove();
    panel = document.createElement('div');
    panel.className = 'pf-model-choices';
    panel.setAttribute('aria-live', 'polite');
    const title = document.createElement('strong');
    title.className = 'pf-model-choices__title';
    title.textContent = `Choose the ${query} model you have:`;
    const grid = document.createElement('div');
    grid.className = 'pf-model-choices__grid';
    choices.forEach((choice) => {
      const link = document.createElement('a');
      link.className = 'pf-model-choices__link';
      link.href = choice.url;
      link.textContent = choice.title;
      grid.appendChild(link);
    });
    panel.append(title, grid);
    form.insertAdjacentElement('afterend', panel);
  };

  const pageMatches = async (query, options = {}) => {
    const raw = text(query);
    const compoundVariants = options.cabInterior && /^\d+$/.test(raw)
      ? ['mx' + raw, 'magnum ' + raw]
      : [];
    const variants = [...new Set([
      raw,
      raw.replace(/\s+/g, ''),
      raw.replace(/([a-z])([0-9])/ig, '$1 $2').replace(/([0-9])([a-z])/ig, '$1 $2'),
      ...compoundVariants
    ].map(text).filter(Boolean))];
    const groups = await Promise.all(variants.map(async (variant) => {
      const endpoint = `/search/suggest.json?q=${encodeURIComponent(variant)}&resources%5Btype%5D=page%2Ccollection&resources%5Blimit%5D=10&resources%5Blimit_scope%5D=each`;
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Model suggestions unavailable');
      const data = await response.json();
      const results = data?.resources?.results || {};
      return [...(results.pages || []), ...(results.collections || [])];
    }));
    const unique = new Map();
    groups.flat().forEach((page) => {
      if (page?.title && page?.url) unique.set(page.url, { title: text(page.title), url: page.url });
    });
    return [...unique.values()];
  };

  const machineRoutes = [
    ['john-deere', 'tractor'], ['john-deere', 'combine'], ['john-deere', 'swather'], ['john-deere', 'sprayer'], ['john-deere', 'forage-harvester'],
    ['new-holland', 'tractor'], ['new-holland', 'combine'], ['new-holland', 'swather'],
    ['international', 'tractor'], ['international', 'combine'], ['international', 'swather'],
    ['case', 'tractor'], ['case', 'combine'], ['case', 'sprayer'], ['case-ih', 'tractor'], ['case-ih', 'combine'], ['case-ih', 'sprayer'],
    ['white', 'tractor'], ['white', 'combine']
  ];

  const titleFromHtml = (source) => text((source.match(/<title[^>]*>([\s\S]*?)<\/title>/i) || [])[1])
    .replace(/\s*(?:\||-|\u2013)\s*(?:PFAB USA|P-Fab Manufacturing).*$/i, '');

  const directCollectionMatches = async (query, scope) => {
    const scopeSlug = text(scope).toLowerCase().replace(/\s+/g, '-');
    const typeMatch = (scopeSlug.match(/(tractor|combine|swather|sprayer|forage-harvester)$/) || [])[1];
    const brandSlug = scopeSlug.replace(/-(tractor|combine|swather|sprayer|forage-harvester)$/, '');
    const routes = scopeSlug
      ? machineRoutes.filter(([brand, type]) => (brand === brandSlug || (brandSlug === 'case' && brand === 'case-ih')) && (!typeMatch || type === typeMatch))
      : machineRoutes;
    const rawModel = text(query).toLowerCase();
    const spacedModel = rawModel.replace(/([a-z])([0-9])/g, '$1-$2').replace(/([0-9])([a-z])/g, '$1-$2').replace(/\s+/g, '-');
    const modelSlugs = [...new Set([rawModel.replace(/\s+/g, '-'), rawModel.replace(/\s+/g, ''), spacedModel, /^\d+$/.test(rawModel) ? `magnum-${rawModel}` : '', /^\d+$/.test(rawModel) ? `mx${rawModel}` : ''].filter(Boolean))];
    const attempts = routes.filter(([, type]) => type).flatMap(([brand, type]) => modelSlugs.map(async (modelSlug) => {
      const url = `/collections/${brand}-${encodeURIComponent(modelSlug)}-${type}`;
      try {
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        if (!response.ok) return null;
        const title = titleFromHtml(await response.text());
        return title && hasExactModel(title, query) ? { title, url } : null;
      } catch (_) {
        return null;
      }
    }));
    return (await Promise.all(attempts)).filter(Boolean);
  };

  /* Shopify's predictive search returns the SEO model pages. Each one contains
     the real model collection link, which is where customers need to land. */
  const collectionForModelPage = async (choice) => {
    if (!choice.url || !choice.url.startsWith('/pages/')) return choice;
    try {
      const response = await fetch(choice.url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return choice;
      const source = await response.text();
      const main = (source.match(/<main\b[^>]*>([\s\S]*?)<\/main>/i) || [])[1] || source;
      const links = [...main.matchAll(/<a\b[^>]*href=["']([^"'#?]*\/collections\/[^"'#?]+)[^"']*["'][^>]*>([\s\S]*?)<\/a>/ig)];
      const preferred = links.find((link) => /browse|shop|view/i.test(text(link[2]))) || links[0];
      return preferred?.[1] ? { ...choice, url: preferred[1] } : choice;
    } catch (_) {
      return choice;
    }
  };


  const collectionPath = (url) => {
    try {
      return new URL(String(url || ''), window.location.origin).pathname;
    } catch (_) {
      return String(url || '').split(/[?#]/)[0];
    }
  };

  const cabInteriorChoices = (choices, query, scope) => {
    const unique = new Map();
    choices.forEach((choice) => {
      const url = collectionPath(choice?.url);
      if (!url.startsWith('/collections/cab-interior-')) return;
      if (!hasExactModel(choice.title, query)) return;
      if (scope && !matchesScope(choice.title, scope)) return;
      if (!unique.has(url)) unique.set(url, { ...choice, url });
    });
    return [...unique.values()];
  };

  const collectionChoices = async (choices) => {
    const resolved = await Promise.all(choices.map(collectionForModelPage));
    const unique = new Map();
    resolved.filter((choice) => choice.url && !choice.url.startsWith('/pages/')).forEach((choice) => {
      /* The 8430 and 8430 MFWD catalog entries are intentionally combined.
         Keep 8430T and 8430 4WD separate, but send either basic 8430 result
         to the single MFWD collection so customers never see duplicate parts. */
      const isCombined8430 = /^John Deere 8430(?: MFWD)? Tractor Parts\b/i.test(text(choice.title));
      const normalized = isCombined8430
        ? { ...choice, title: 'John Deere 8430 MFWD Tractor Parts', url: '/collections/john-deere-8430-mfwd-tractor' }
        : choice;
      /* A model can have both an SEO page and a direct collection result.
         Use the displayed model title as the identity so those two routes
         never show the customer duplicate choices. */
      const identity = text(normalized.title)
        .replace(/\s*(?:\||-|\u2013|\u2014)\s*(?:PFAB USA|P-Fab Manufacturing).*$/i, '')
        .toLowerCase();
      if (!unique.has(identity)) unique.set(identity, normalized);
    });
    return [...unique.values()];
  };

  window.PfabModelChooser = {
    attach(form, options = {}) {
      if (!form || form.dataset.pfabModelChooserReady === 'true') return;
      form.dataset.pfabModelChooserReady = 'true';
      ensureStyles();
      const input = form.querySelector('input[type="text"], input[type="search"]');
      const error = form.querySelector('[data-model-error]') || form.querySelector('.pf-model-nav__error');
      const scope = text(options.scope || '');
      const pageOnlyFallback = options.pageOnlyFallback !== false;
      const cabInterior = options.cabInterior === true;

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const query = text(input?.value);
        if (!query) {
          if (error) error.style.display = 'block';
          input?.focus();
          return;
        }
        if (error) error.style.display = 'none';
        const fallbackUrl = scopedSearchUrl(query, scope, pageOnlyFallback);
        const button = form.querySelector('button[type="submit"]');
        const original = button?.textContent;
        if (button) { button.disabled = true; button.textContent = 'Finding...'; }
        try {
          /* Search the storefront model pages and the actual collections for
             every manufacturer. collectionChoices() removes any duplicate
             route, so a shared model such as 8770 can show both valid makes
             while a single John Deere model only appears once. */
          const [all, direct] = await Promise.all([
            pageMatches(query, { cabInterior }),
            cabInterior ? Promise.resolve([]) : directCollectionMatches(query, scope)
          ]);
          let choices;
          if (cabInterior) {
            /* Cab Interior uses only imported manual cab-interior-* collections. */
            choices = cabInteriorChoices(all, query, scope);
          } else {
            const exact = all.filter((choice) => hasExactModel(choice.title, query));
            const scoped = scope ? exact.filter((choice) => matchesScope(choice.title, scope)) : exact;
            const matchedPages = scope ? scoped : exact;
            const resolvedChoices = await collectionChoices([...matchedPages, ...direct]);
            choices = (scope ? resolvedChoices.filter((choice) => matchesScope(choice.title, scope)) : resolvedChoices)
              .filter((choice) => !collectionPath(choice.url).startsWith('/collections/cab-interior-'));
          }
          if (choices.length === 1) {
            window.location.assign(choices[0].url);
            return;
          }
          if (choices.length > 1) {
            renderChoices(form, query, choices, fallbackUrl);
            return;
          }
          if (scope) {
            if (error) {
              error.textContent = `No exact ${scope} model found for “${query}”.`;
              error.style.display = 'block';
            }
            return;
          }
          window.location.assign(fallbackUrl);
        } catch (_) {
          if (cabInterior) {
            if (error) {
              error.textContent = 'No exact ' + scope + ' Cab Interior model found for “' + query + '”.';
              error.style.display = 'block';
            }
            return;
          }
          window.location.assign(fallbackUrl);
        } finally {
          if (button) { button.disabled = false; button.textContent = original; }
        }
      });
    }
  };
})();
